import {
  getBrazilCivilDate,
  getBrazilCurrentCompetenceMonth,
  getBrazilMonthBounds,
  getBrazilPreviousCompetenceMonth,
} from '../utils/competence';

export interface DatePresetOption {
  value: string;
  label: string;
  resolveRange?: () => { start: string; end: string };
}

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export function buildLastNDaysPreset(days: number, value: string, label: string): DatePresetOption {
  return {
    value,
    label,
    resolveRange: () => ({ start: daysAgo(days), end: todayStr() }),
  };
}

export const DEFAULT_DATE_PRESET_OPTIONS: DatePresetOption[] = [
  {
    value: 'yesterday',
    label: 'Ontem',
    resolveRange: () => ({ start: daysAgo(1), end: daysAgo(1) }),
  },
  buildLastNDaysPreset(7, 'last7', 'Últimos 7 dias'),
  buildLastNDaysPreset(30, 'last30', 'Últimos 30 dias'),
  { value: 'custom', label: 'Personalizado' },
];

/** Período padrão das telas /sys/logs e /sys/payment-errors. */
export const SYS_DATE_PRESET_OPTIONS: DatePresetOption[] = [
  buildLastNDaysPreset(7, 'last7', 'Últimos 7 dias'),
  buildLastNDaysPreset(30, 'last30', 'Últimos 30 dias'),
  buildLastNDaysPreset(90, 'last90', 'Últimos 90 dias'),
  { value: 'custom', label: 'Personalizado' },
];

export function getSysDateDefaultRange(): { start: string; end: string } {
  const last90 = SYS_DATE_PRESET_OPTIONS.find((preset) => preset.value === 'last90');
  return last90?.resolveRange?.() ?? buildLastNDaysPreset(90, 'last90', 'Últimos 90 dias').resolveRange!();
}

export function isSysDateDefaultRange(start: string, end: string): boolean {
  const { start: defaultStart, end: defaultEnd } = getSysDateDefaultRange();
  return start === defaultStart && end === defaultEnd;
}

/** Período do dashboard comercial no menu Metas. */
export const COMMERCIAL_GOALS_PERIOD_PRESETS: DatePresetOption[] = [
  {
    value: 'today',
    label: 'Hoje',
    resolveRange: () => {
      const today = getBrazilCivilDate();
      return { start: today, end: today };
    },
  },
  {
    value: 'last7',
    label: 'Últimos 7 dias',
    resolveRange: () => ({ start: getBrazilCivilDate(new Date(), -6), end: getBrazilCivilDate() }),
  },
  {
    value: 'currentMonth',
    label: 'Mês atual',
    resolveRange: () => {
      const bounds = getBrazilMonthBounds(getBrazilCurrentCompetenceMonth());
      const today = getBrazilCivilDate();
      return bounds ?? { start: today, end: today };
    },
  },
  {
    value: 'previousMonth',
    label: 'Mês anterior',
    resolveRange: () => {
      const bounds = getBrazilMonthBounds(getBrazilPreviousCompetenceMonth());
      const today = getBrazilCivilDate();
      return bounds ?? { start: today, end: today };
    },
  },
  { value: 'custom', label: 'Personalizado' },
];

/** Período do dashboard Fotos recentes (padrão: sem filtro = tudo). */
export const RECENT_BODY_PHOTOS_PERIOD_PRESETS: DatePresetOption[] = [
  {
    value: 'today',
    label: 'Hoje',
    resolveRange: () => {
      const today = getBrazilCivilDate();
      return { start: today, end: today };
    },
  },
  {
    value: 'last7',
    label: 'Últimos 7 dias',
    resolveRange: () => ({
      start: getBrazilCivilDate(new Date(), -6),
      end: getBrazilCivilDate(),
    }),
  },
  {
    value: 'last30',
    label: 'Últimos 30 dias',
    resolveRange: () => ({
      start: getBrazilCivilDate(new Date(), -29),
      end: getBrazilCivilDate(),
    }),
  },
  { value: 'custom', label: 'Personalizado' },
];

/** Período da aba Pagamentos na home. */
export const PAYMENTS_RECEIVED_PERIOD_PRESETS: DatePresetOption[] = [
  {
    value: 'today',
    label: 'Hoje',
    resolveRange: () => {
      const today = getBrazilCivilDate();
      return { start: today, end: today };
    },
  },
  {
    value: 'yesterday',
    label: 'Ontem',
    resolveRange: () => {
      const yesterday = getBrazilCivilDate(new Date(), -1);
      return { start: yesterday, end: yesterday };
    },
  },
  {
    value: 'last7',
    label: 'Últimos 7 dias',
    resolveRange: () => ({
      start: getBrazilCivilDate(new Date(), -6),
      end: getBrazilCivilDate(),
    }),
  },
  { value: 'custom', label: 'Personalizado' },
];

export const COMMERCIAL_JOINED_AT_DATE_PRESETS: DatePresetOption[] = [
  buildLastNDaysPreset(7, 'last7', 'Últimos 7 dias'),
  buildLastNDaysPreset(30, 'last30', 'Últimos 30 dias'),
  { value: 'custom', label: 'Personalizado' },
];

export const ORCHESTRATOR_PAYMENT_DATE_PRESETS: DatePresetOption[] = [
  buildLastNDaysPreset(7, 'last7', 'Últimos 7 dias'),
  buildLastNDaysPreset(30, 'last30', 'Últimos 30 dias'),
  { value: 'custom', label: 'Personalizado' },
];

export const ORCHESTRATOR_LAST_WORKOUT_DATE_PRESETS: DatePresetOption[] = [
  buildLastNDaysPreset(30, 'last30', 'Últimos 30 dias'),
  buildLastNDaysPreset(7, 'last7', 'Últimos 7 dias'),
  buildLastNDaysPreset(15, 'last15', 'Últimos 15 dias'),
  { value: 'custom', label: 'Personalizado' },
];

export function getOrchestratorLastWorkoutDefaultRange(): { start: string; end: string } {
  const last30Preset = ORCHESTRATOR_LAST_WORKOUT_DATE_PRESETS.find((preset) => preset.value === 'last30');
  return last30Preset?.resolveRange?.() ?? buildLastNDaysPreset(30, 'last30', 'Últimos 30 dias').resolveRange!();
}

export function isOrchestratorLastWorkoutDefaultRange(start: string, end: string): boolean {
  const { start: defaultStart, end: defaultEnd } = getOrchestratorLastWorkoutDefaultRange();
  return start === defaultStart && end === defaultEnd;
}

export function detectDatePreset(
  start: string,
  end: string,
  options: DatePresetOption[],
): string {
  if (!start || !end) {
    return '';
  }

  for (const option of options) {
    if (option.value === 'custom' || !option.resolveRange) {
      continue;
    }

    const range = option.resolveRange();
    if (range.start === start && range.end === end) {
      return option.value;
    }
  }

  return 'custom';
}

export function formatDatePresetShort(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y.slice(2)}`;
}

function localIsoDate(offsetDays = 0): string {
  const date = new Date();
  date.setDate(date.getDate() + offsetDays);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export const DASHBOARD_DUE_DATE_PRESETS: DatePresetOption[] = [
  {
    value: 'yesterday',
    label: 'Ontem',
    resolveRange: () => ({ start: localIsoDate(-1), end: localIsoDate(-1) }),
  },
  {
    value: 'today',
    label: 'Hoje',
    resolveRange: () => ({ start: localIsoDate(0), end: localIsoDate(0) }),
  },
  {
    value: 'tomorrow',
    label: 'Amanhã',
    resolveRange: () => ({ start: localIsoDate(1), end: localIsoDate(1) }),
  },
  {
    value: 'last7',
    label: 'Últimos 7 dias',
    resolveRange: () => ({ start: localIsoDate(-6), end: localIsoDate(0) }),
  },
  {
    value: 'next7',
    label: 'Próximos 7 dias',
    resolveRange: () => ({ start: localIsoDate(0), end: localIsoDate(6) }),
  },
  { value: 'custom', label: 'Personalizado' },
];

/** Dias vencidos da aba A vencer (padrão: Todos). */
export const PAYMENTS_DUE_OVERDUE_DAY_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: '7', label: '7 dias' },
  { value: '15', label: '15 dias' },
  { value: '30', label: '30 dias' },
] as const;

export type PaymentsDueOverdueDays = typeof PAYMENTS_DUE_OVERDUE_DAY_OPTIONS[number]['value'];

/** Data de vencimento da aba A vencer (dia do pagamento; padrão: sem filtro). */
export const PAYMENTS_DUE_DATE_PRESETS: DatePresetOption[] = [
  {
    value: 'today',
    label: 'Hoje',
    resolveRange: () => {
      const today = getBrazilCivilDate();
      return { start: today, end: today };
    },
  },
  {
    value: 'yesterday',
    label: 'Ontem',
    resolveRange: () => {
      const yesterday = getBrazilCivilDate(new Date(), -1);
      return { start: yesterday, end: yesterday };
    },
  },
  { value: 'custom', label: 'Personalizado' },
];

export function formatPaymentsDueRangeLabel(start: string, end: string): string {
  const preset = detectDatePreset(start, end, PAYMENTS_DUE_DATE_PRESETS);
  const option = PAYMENTS_DUE_DATE_PRESETS.find((item) => item.value === preset);
  if (option && preset !== 'custom') {
    return option.label;
  }

  if (start === end) {
    return formatDatePresetShort(start);
  }

  return `${formatDatePresetShort(start)} – ${formatDatePresetShort(end)}`;
}

export function formatPaymentsDueOverdueLabel(value: string): string {
  const option = PAYMENTS_DUE_OVERDUE_DAY_OPTIONS.find((item) => item.value === value);
  return option?.label ?? value;
}

export function formatDashboardDueRangeLabel(start: string, end: string): string {
  const preset = detectDatePreset(start, end, DASHBOARD_DUE_DATE_PRESETS);
  const option = DASHBOARD_DUE_DATE_PRESETS.find((item) => item.value === preset);
  if (option && preset !== 'custom') {
    return option.label;
  }

  return `${formatDatePresetShort(start)} – ${formatDatePresetShort(end)}`;
}
